import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getSymbioticEnergyList,
  addEnergyToBusiness,
  updateEnergy,
  deleteEnergy,
} from "../../../../../../apis/Business/EnergyApi";
import { getGlobalIdematSymbioticEnergy } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const SymbioticEnergyTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const [dropdownUsedFor, setDropdownUsedFor] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  useEffect(() => {
    getGlobalIdematSymbioticEnergy({ setDropdown: setDropdownUsedFor });
  }, [t]);

  const fetchData = useCallback(() => {
    getSymbioticEnergyList({
      businessId,
      setObjects: setData,
    });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue = row["type.value"]?.value || row.type?.value;
        if (materialValue) {
          const selectedMaterial = dropdownUsedFor.find(
            (option) => option?.value === materialValue
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownUsedFor]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "type.value": {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit,
              }));
            }
          }
        },
        dropdownDataKey: "type.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      let typeObj = {};
      if (editedItem["type.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type.value"].value
        );
      } else if (editedItem["type"].value) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type"].value
        );
      }

      const typedb =
        typeObj && typeObj.typeDB
          ? typeObj.typeDB
          : editedItem.type && typeof editedItem.type === "string"
          ? editedItem.type
          : data["typedb"];
      updateEnergy({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        category: "symbiotic",
        price: editedItem["price_invoice"],
        invoiceDate: editedItem["invoice_date"],
        invoiceNo: editedItem["invoice_number"],
        type: editedItem["type.value"]
          ? editedItem["type.value"].value
          : editedItem["type"]
          ? editedItem["type"].value
          : null,
        source: null,
        typedb: typedb,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, dropdownUsedFor, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      let typeObj = {};
      if (newItem["type.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === newItem["type.value"].value
        );
      }
      addEnergyToBusiness({
        businessId,
        ...newItem,
        category: "symbiotic",
        price: newItem["price_invoice"],
        invoiceDate: newItem["invoice_date"],
        invoiceNo: newItem["invoice_number"],
        type: newItem["type.value"] ? newItem["type.value"].value : null,
        typedb: typeObj ? typeObj.typeDB : newItem.type,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, dropdownUsedFor, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteEnergy({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "110px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "110px",
      },
      {
        accessor: "type.value",
        header: `${t("PURCHASED_ENERGY")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
      },
      {
        accessor: "single_source",
        header: t("SOURCE"),
        type: "switch",
        disableSortBy: true,
        width: "120px",
      },
      {
        accessor: "qty",
        header: (props) => {
          return `${t("QUANTITY")}*`;
        },
        type: "number",
        tipHeader: "Tip",
        tipBody: t("TIP_ENERGY_QTY"),
        disableSortBy: true,
        placeholder: "0",
        width: "110px",
        unit: (rowId, rowData) => {
          const rowType =
            rowData?.type?.value || rowData?.["type.value"]?.value;
          return rowType
            ? dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId] || "xx";
        },
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        width: "110px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownUsedFor, t, unitMaterials]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "type.value": dropdownUsedFor,
    }),
    [dropdownUsedFor]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default SymbioticEnergyTable;
